import React, { useEffect, useState } from 'react';
import { X, Search, Loader, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ArtworkPickerModal — searchable browser over the entire live artwork
 * catalog. Used by the newsletter curation UI so the admin can hand-pick
 * pieces that aren't in the auto-selected "last 60 days" pool.
 *
 * Props:
 *   • open        — bool
 *   • onClose     — () => void
 *   • onPick      — (artwork) => void  called for each selected artwork
 *   • existingIds — string[]  already-in-newsletter ids (dim them out)
 *   • maxSelect   — number    remaining slots (0 disables selection)
 */
const ArtworkPickerModal = ({ open, onClose, onPick, existingIds = [], maxSelect = 9 }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState({}); // id → artwork (batch picks in one session)

  useEffect(() => {
    if (!open) return undefined;
    setPicked({});
    const ctrl = new AbortController();
    const run = async () => {
      setLoading(true);
      try {
        // Public /api/artworks supports q= full-text and returns available works
        // by default. Cap at 60 rows to keep the modal snappy — admin refines
        // with search.
        const url = `${API_URL}/api/artworks?limit=60${q ? `&q=${encodeURIComponent(q)}` : ''}`;
        const res = await fetch(url, { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e.name !== 'AbortError') toast.error(`Search failed: ${e.message}`);
      } finally {
        setLoading(false);
      }
    };
    const t = setTimeout(run, q ? 250 : 0);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q, open]);

  if (!open) return null;

  const pickedCount = Object.keys(picked).length;
  const remaining = Math.max(0, maxSelect - pickedCount);

  const toggle = (a) => {
    if (existingIds.includes(a.id)) return;
    setPicked((prev) => {
      const next = { ...prev };
      if (next[a.id]) {
        delete next[a.id];
      } else {
        if (Object.keys(next).length >= maxSelect) {
          toast.error(`Only ${maxSelect} slot${maxSelect === 1 ? '' : 's'} available`);
          return prev;
        }
        next[a.id] = a;
      }
      return next;
    });
  };

  const confirm = () => {
    Object.values(picked).forEach((a) => onPick(a));
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="artwork-picker-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="artwork-picker-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-light">Pick artworks from the library</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {remaining} slot{remaining === 1 ? '' : 's'} remaining · {pickedCount} selected
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
            data-testid="artwork-picker-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search bar */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center gap-2 border border-gray-300 rounded px-3 py-2 focus-within:border-black">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              autoFocus
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search artist, title, medium…"
              className="flex-1 outline-none text-sm bg-transparent"
              data-testid="artwork-picker-search"
            />
            {loading && <Loader className="w-4 h-4 animate-spin text-gray-400" />}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {results.length === 0 && !loading ? (
            <div className="text-center text-gray-500 py-16 text-sm">
              No artworks found. Try a different search.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="artwork-picker-grid">
              {results.map((a) => {
                const already = existingIds.includes(a.id);
                const isPicked = !!picked[a.id];
                const img = a.imageUrl?.startsWith('http') ? a.imageUrl : `${API_URL}${a.imageUrl || ''}`;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => toggle(a)}
                    disabled={already}
                    className={`relative text-left group rounded overflow-hidden border transition-all ${
                      already
                        ? 'border-gray-200 opacity-40 cursor-not-allowed'
                        : isPicked
                        ? 'border-black ring-2 ring-black'
                        : 'border-gray-200 hover:border-black'
                    }`}
                    data-testid={`artwork-picker-item-${a.id}`}
                  >
                    <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
                      {a.imageUrl ? (
                        <img
                          src={img}
                          alt={a.title}
                          loading="lazy"
                          className="w-full h-full object-contain pointer-events-none"
                        />
                      ) : (
                        <div className="text-xs text-gray-400">No image</div>
                      )}
                    </div>
                    <div className="p-2 text-xs">
                      <div className="truncate font-medium text-gray-900" title={a.artistName}>
                        {a.artistName || '—'}
                      </div>
                      <div className="truncate text-gray-500" title={a.title}>
                        {a.title || 'Untitled'}
                      </div>
                    </div>
                    {isPicked && (
                      <div className="absolute top-2 right-2 bg-black text-white rounded-full p-1">
                        <Check className="w-3 h-3" />
                      </div>
                    )}
                    {already && (
                      <div className="absolute top-2 right-2 bg-gray-800 text-white text-[10px] px-1.5 py-0.5 rounded">
                        In list
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-500">
            {results.length} result{results.length === 1 ? '' : 's'}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-white"
              data-testid="artwork-picker-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={pickedCount === 0}
              className="px-4 py-2 text-sm bg-black text-white rounded hover:bg-gray-800 disabled:opacity-40 flex items-center gap-2"
              data-testid="artwork-picker-confirm"
            >
              <Check className="w-4 h-4" />
              Add {pickedCount || ''} to newsletter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtworkPickerModal;
